/**
 * Dehla Pakad bot.
 *
 * - Trump: the suit with the best length-and-honours mix among the first five cards.
 * - Play: partnership-aware and ten-aware. A trick holding a ten is worth fighting for, so the bot wins it as cheaply
 *   as it can (ruffing if necessary), feeds tens to a partner who is certain to win, and never leads a ten unless it
 *   is the master of its suit. Tricks with no ten are only taken when that is cheap. NORMAL and above track played
 *   cards to recognise masters.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  dehlaPakadBestSuit,
  dehlaPakadIsTen,
  dehlaPakadPartnerOf,
  dehlaPakadTrickWinner,
  type DehlaPakadAction,
  type DehlaPakadLegal,
  type DehlaPakadTrickPlay,
  type DehlaPakadView,
} from './engine.js';

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: DehlaPakadView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(buildDeck().filter((c) => !seen.has(c))) };
}

/** True when no unseen card of the same suit outranks it (ignoring trump from other suits). */
function isMaster(card: CardId, k: Knowledge): boolean {
  const c = parseCard(card);
  for (const u of k.unseen) {
    const x = parseCard(u);
    if (x.suit === c.suit && x.rank > c.rank) return false;
  }
  return true;
}

function wouldWin(trick: readonly DehlaPakadTrickPlay[], seat: number, card: CardId, trump: Suit): boolean {
  if (trick.length === 0) return true;
  return dehlaPakadTrickWinner([...trick, { seat, card }], trump) === seat;
}

function chooseCard(view: DehlaPakadView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const me = view.seat!;
  const partner = dehlaPakadPartnerOf(me);
  const trump = view.trump!;
  const trick = view.trick;
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  // Cost of spending a card: trumps are dear, tens are dear (they are the prize), otherwise rank.
  const power = (id: CardId) => {
    const c = parseCard(id);
    return (c.suit === trump ? 100 : 0) + c.rank;
  };
  const cost = (id: CardId) => power(id) + (dehlaPakadIsTen(id) ? 40 : 0);
  const lowest = (cards: CardId[]) => [...cards].sort((a, b) => cost(a) - cost(b))[0]!;
  const highest = (cards: CardId[]) => [...cards].sort((a, b) => power(b) - power(a))[0]!;
  const dump = (cards: CardId[]) => {
    const plain = cards.filter((c) => !dehlaPakadIsTen(c));
    const pool = plain.length ? plain : cards;
    const side = pool.filter((c) => parseCard(c).suit !== trump);
    if (side.length === 0) return lowest(pool);
    // Shed from the suit held longest, lowest first — keeps short suits for ruffing.
    const counts = new Map<Suit, number>();
    side.forEach((c) => counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1));
    const longest = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]![0];
    return lowest(side.filter((c) => parseCard(c).suit === longest));
  };

  if (trick.length === 0) {
    // Leading: a master side-suit card that is not a ten, then a safe ten if it is the master, then a low card from the shortest side suit.
    const side = legal.filter((c) => parseCard(c).suit !== trump);
    const masters = k ? side.filter((c) => isMaster(c, k)) : side.filter((c) => parseCard(c).rank === 14);
    const plainMasters = masters.filter((c) => !dehlaPakadIsTen(c));
    if (plainMasters.length) return highest(plainMasters);
    if (masters.length) return highest(masters);
    const plain = legal.filter((c) => !dehlaPakadIsTen(c));
    const trumps = plain.filter((c) => parseCard(c).suit === trump);
    if (k && trumps.length >= 4) {
      const top = trumps.filter((c) => isMaster(c, k));
      if (top.length) return highest(top);
    }
    const plainSide = plain.filter((c) => parseCard(c).suit !== trump);
    if (plainSide.length) {
      const counts = new Map<Suit, number>();
      plainSide.forEach((c) => counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1));
      const shortSuit = [...counts.entries()].sort((a, b) => a[1] - b[1])[0]![0];
      return lowest(plainSide.filter((c) => parseCard(c).suit === shortSuit));
    }
    return lowest(plain.length ? plain : legal);
  }

  const winnerSeat = dehlaPakadTrickWinner(trick, trump);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const last = trick.length === 3;
  const tensInTrick = trick.filter((p) => dehlaPakadIsTen(p.card)).length;
  if (winnerSeat === partner) {
    // Partner is winning. If the trick is safe, feed it a ten; otherwise keep our cheap cards.
    const safe = last || (k !== null && isMaster(winnerCard, k) && parseCard(winnerCard).suit !== trump) || (k !== null && parseCard(winnerCard).suit === trump && isMaster(winnerCard, k));
    if (safe) {
      const tens = legal.filter(dehlaPakadIsTen);
      if (tens.length) return tens[0]!;
      return dump(legal);
    }
    const over = legal.filter((c) => wouldWin(trick, me, c, trump) && parseCard(c).suit === parseCard(winnerCard).suit);
    if (k && over.length && tensInTrick > 0 && parseCard(winnerCard).rank < 10) return lowest(over);
    return dump(legal);
  }
  const winners = legal.filter((c) => wouldWin(trick, me, c, trump));
  if (winners.length === 0) return dump(legal);
  // A ten in the pot is worth a trump; an empty pot is only worth taking cheaply or from last seat.
  const cheap = winners.filter((c) => parseCard(c).suit !== trump);
  if (tensInTrick > 0 || last) {
    const pool = last && tensInTrick === 0 && cheap.length ? cheap : winners;
    const safeWinners = !last && k ? pool.filter((c) => isMaster(c, k) || parseCard(c).suit === trump) : pool;
    return lowest(safeWinners.length ? safeWinners : pool);
  }
  if (cheap.length) {
    const safeCheap = k ? cheap.filter((c) => isMaster(c, k)) : cheap;
    if (safeCheap.length) return lowest(safeCheap.filter((c) => !dehlaPakadIsTen(c)).length ? safeCheap.filter((c) => !dehlaPakadIsTen(c)) : safeCheap);
  }
  return dump(legal);
}

export const dehlaPakadBot: BotEngine<DehlaPakadView, DehlaPakadLegal, DehlaPakadAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'trump') return { type: 'trump', suit: dehlaPakadBestSuit(view.hand) };
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    const tens = view.tensWon[team]! - view.tensWon[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 10 + tens / 16));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'trump') return { type: 'trump', suit: dehlaPakadBestSuit(view.hand) };
    if (legal.kind === 'play') {
      const trump = view.trump;
      const weight = (id: CardId) => (parseCard(id).suit === trump ? 100 : 0) + (dehlaPakadIsTen(id) ? 20 : 0) + parseCard(id).rank;
      return { type: 'play', card: [...legal.cards].sort((a, b) => weight(a) - weight(b))[0]! };
    }
    throw new Error('No legal action');
  },
};
